import { getPagesApi, getPostsApi } from '@/api-calls/posts/get'
import { deletePostApi } from '@/api-calls/posts/mutate'
import { getTagsApi } from '@/api-calls/tags/get'
import { useSite } from '@/hooks/use-site'
import {
  Badge,
  Button,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Switch,
  Textarea,
} from '@blogging/ui'
import { slugify } from '@blogging/utils'
import { useFetch, useMutation, useQueryClient } from '@ngtrvu/use-api'
import { format, isValid, parse, setHours, setMinutes } from 'date-fns'
import {
  Clock,
  Loader2,
  PanelRightClose,
  Search,
  Tag,
  Trash2,
  X,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

interface PanelProps {
  toggleSidebar: () => void
  postId?: string
  siteId: string
  type: 'post' | 'page'
  title?: string
  // initialData is now handled by the parent form context defaultValues
}

interface TagItem {
  id: string
  name: string
  slug: string
}

export default function Panel({
  toggleSidebar,
  postId,
  siteId,
  type,
  title,
}: PanelProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { siteUrlWithScheme } = useSite()
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false)

  // Use context instead of local form
  const { control, setValue, getValues, watch } = useFormContext()

  // Fetch available tags
  const { data: availableTags } = useFetch(getTagsApi, {
    resourceName: 'tags',
    initialParams: { siteId },
  })

  const invalidatePostsList = () => {
    if (type === 'post') {
      queryClient.invalidateQueries({
        queryKey: [getPostsApi.apiName, { siteId }],
      })
    } else {
      queryClient.invalidateQueries({
        queryKey: [getPagesApi.apiName, { siteId }],
      })
    }
  }

  const { mutate: deletePost, isPending: isDeleting } = useMutation(
    deletePostApi,
    {
      resourceName: 'post',
      onSuccess: () => {
        invalidatePostsList()
        toast.success('Post deleted successfully!')
      },
      onError: (error: any) => {
        const { message } = error
        toast.error(message || 'Failed to delete post')
      },
    },
  )

  // Local state for date input to allow free editing
  const [dateInputValue, setDateInputValue] = useState(() => {
    const publishedAt = getValues('publishedAt')
    return publishedAt ? format(publishedAt, 'yyyy-MM-dd') : ''
  })

  // Local state for time input to allow free editing
  const [timeInputValue, setTimeInputValue] = useState(() => {
    const publishedAt = getValues('publishedAt')
    return publishedAt ? format(publishedAt, 'HH:mm') : ''
  })

  // Watch for form value changes to sync local state when initial data loads
  const watchedPublishedAt = watch('publishedAt')

  useEffect(() => {
    if (watchedPublishedAt) {
      setDateInputValue(format(watchedPublishedAt, 'yyyy-MM-dd'))
      setTimeInputValue(format(watchedPublishedAt, 'HH:mm'))
    } else {
      setDateInputValue('')
      setTimeInputValue('')
    }
  }, [watchedPublishedAt])

  const handleDateChange = (newDate: Date | undefined) => {
    if (!newDate) {
      setValue('publishedAt', undefined, { shouldDirty: true })
      setDateInputValue('')
      return
    }

    // If we have an existing time, preserve it
    const existingDate = getValues('publishedAt')
    if (existingDate) {
      newDate.setHours(existingDate.getHours(), existingDate.getMinutes())
    }

    setValue('publishedAt', newDate, { shouldDirty: true })
    setDateInputValue(format(newDate, 'yyyy-MM-dd'))
  }

  const handleDateInputChange = (inputValue: string) => {
    setDateInputValue(inputValue)

    // Only try to parse if it looks like a complete date
    if (inputValue.length === 10 && inputValue.includes('-')) {
      const parsed = parse(inputValue, 'yyyy-MM-dd', new Date())
      if (isValid(parsed)) {
        // Preserve existing time if any
        const existingDate = getValues('publishedAt')
        if (existingDate) {
          parsed.setHours(existingDate.getHours(), existingDate.getMinutes())
        }
        setValue('publishedAt', parsed, { shouldDirty: true })
      }
    }
  }

  const handleDateInputBlur = () => {
    // Parse on blur regardless of length
    const parsed = parse(dateInputValue, 'yyyy-MM-dd', new Date())
    if (isValid(parsed)) {
      // Preserve existing time if any
      const existingDate = getValues('publishedAt')
      if (existingDate) {
        parsed.setHours(existingDate.getHours(), existingDate.getMinutes())
      }
      setValue('publishedAt', parsed, { shouldDirty: true })
      setDateInputValue(format(parsed, 'yyyy-MM-dd'))
    } else {
      const currentDate = getValues('publishedAt')
      if (currentDate) {
        // Reset to current field value if invalid
        setDateInputValue(format(currentDate, 'yyyy-MM-dd'))
      }
    }
  }

  const handleTimeInputChange = (timeValue: string) => {
    // Always update local state to allow free typing/deleting
    setTimeInputValue(timeValue)
  }

  const handleTimeInputBlur = () => {
    // Only parse and validate on blur
    if (!timeInputValue) {
      // Allow clearing the time
      return
    }

    // Try to parse the time
    if (timeInputValue.includes(':')) {
      const parts = timeInputValue.split(':')
      const hours = parseInt(parts[0], 10)
      const minutes = parseInt(parts[1], 10)

      if (!isNaN(hours) && !isNaN(minutes) && hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        const existingDate = getValues('publishedAt')
        let date = existingDate ? new Date(existingDate) : new Date()
        date = setHours(setMinutes(date, minutes), hours)
        setValue('publishedAt', date, { shouldDirty: true })
        setDateInputValue(format(date, 'yyyy-MM-dd'))
        // Format to HH:mm on blur
        setTimeInputValue(format(date, 'HH:mm'))
        return
      }
    }

    // If invalid, reset to current value
    const currentDate = getValues('publishedAt')
    if (currentDate) {
      setTimeInputValue(format(currentDate, 'HH:mm'))
    } else {
      setTimeInputValue('')
    }
  }

  return (
    <div className="border-l p-8 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-lg">Post settings</h2>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="border border-gray-200 shadow"
          onClick={toggleSidebar}
          aria-label="Open sidebar"
          tabIndex={7}
        >
          <PanelRightClose className="h-5 w-5" />
        </Button>
      </div>

      <div className="space-y-6">
        <FormField
          control={control}
          name="slug"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">URL</FormLabel>
              <FormControl>
                <Input
                  placeholder=""
                  className="h-9"
                  {...field}
                  onBlur={(e) => {
                    field.onBlur()
                    if (e.target.value === '') {
                      setValue('slug', slugify(title || '', false), {
                        shouldDirty: true,
                      })
                    } else {
                      setValue('slug', slugify(e.target.value, false), {
                        shouldDirty: true,
                      })
                    }
                  }}
                />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground break-all">
                {siteUrlWithScheme}/{field.value}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="publishedAt"
          render={({ field }) => {
            // Get timezone offset
            const timezoneOffset = new Date().getTimezoneOffset()
            const timezoneSign = timezoneOffset <= 0 ? '+' : '-'
            const timezoneHours = Math.floor(Math.abs(timezoneOffset) / 60)
            const timezoneString = `${timezoneSign}${String(timezoneHours).padStart(2, '0')}`

            return (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel className="text-sm font-medium">
                    Publish date
                  </FormLabel>
                </div>

                <div className="flex flex-row md:gap-2 gap-1">
                  <InputGroup>
                    <InputGroupInput
                      type="text"
                      placeholder="YYYY-MM-DD"
                      className="h-9 text-sm flex-1"
                      value={dateInputValue}
                      onChange={(e) => {
                        const value = e.target.value
                        setDateInputValue(value)
                      }}
                      onBlur={handleDateInputBlur}
                    />
                  </InputGroup>
                  <InputGroup>
                    <InputGroupInput
                      placeholder="00:00"
                      value={timeInputValue}
                      onChange={(e) => handleTimeInputChange(e.target.value)}
                      onBlur={handleTimeInputBlur}
                    />
                    <InputGroupAddon>
                      <Clock />
                    </InputGroupAddon>
                    <InputGroupAddon align="inline-end">
                      {timezoneString}
                    </InputGroupAddon>
                  </InputGroup>
                </div>
                <FormMessage />
              </FormItem>
            )
          }}
        />

        <FormField
          control={control}
          name="excerpt"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">Excerpt</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Write an excerpt (optional)"
                  className="h-24 resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="featured"
          render={({ field }) => (
            <FormItem className="flex gap-4 items-center justify-between">
              <div className="space-y-0.5">
                <FormLabel className="text-sm font-medium">
                  Feature this post
                </FormLabel>
                <FormDescription className="text-xs text-muted-foreground">
                  Featured posts appear prominently on your site
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="tagIds"
          render={({ field }) => {
            const selectedTagIds: string[] = field.value || []
            const selectedTags =
              availableTags?.filter((tag: TagItem) =>
                selectedTagIds.includes(tag.id),
              ) || []
            const unselectedTags =
              availableTags?.filter(
                (tag: TagItem) => !selectedTagIds.includes(tag.id),
              ) || []

            const handleAddTag = (tagId: string) => {
              const newTagIds = [...selectedTagIds, tagId]
              setValue('tagIds', newTagIds, { shouldDirty: true })
              setIsTagDropdownOpen(false)
            }

            const handleRemoveTag = (tagId: string) => {
              const newTagIds = selectedTagIds.filter((id) => id !== tagId)
              setValue('tagIds', newTagIds, { shouldDirty: true })
            }

            return (
              <FormItem>
                <FormLabel className="text-sm font-medium">Tags</FormLabel>
                <div className="space-y-2">
                  {selectedTags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {selectedTags.map((tag: TagItem) => (
                        <Badge
                          key={tag.id}
                          variant="secondary"
                          className="flex items-center gap-1 pr-1"
                        >
                          {tag.name}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag.id)}
                            className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="relative">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full justify-start text-muted-foreground"
                      onClick={() => setIsTagDropdownOpen(!isTagDropdownOpen)}
                    >
                      <Tag className="h-4 w-4 mr-2" />
                      Add tag...
                    </Button>
                    {isTagDropdownOpen && unselectedTags.length > 0 && (
                      <div className="absolute z-50 top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto rounded-md border bg-popover p-1 shadow-md">
                        {unselectedTags.map((tag: TagItem) => (
                          <button
                            key={tag.id}
                            type="button"
                            className="w-full text-left px-2 py-1.5 text-sm rounded-sm hover:bg-accent cursor-pointer"
                            onClick={() => handleAddTag(tag.id)}
                          >
                            {tag.name}
                          </button>
                        ))}
                      </div>
                    )}
                    {isTagDropdownOpen &&
                      unselectedTags.length === 0 &&
                      availableTags?.length > 0 && (
                        <div className="absolute z-50 top-full left-0 right-0 mt-1 rounded-md border bg-popover p-2 shadow-md">
                          <p className="text-sm text-muted-foreground">
                            All tags added
                          </p>
                        </div>
                      )}
                    {isTagDropdownOpen &&
                      (!availableTags || availableTags.length === 0) && (
                        <div className="absolute z-50 top-full left-0 right-0 mt-1 rounded-md border bg-popover p-2 shadow-md">
                          <p className="text-sm text-muted-foreground">
                            No tags available
                          </p>
                        </div>
                      )}
                  </div>
                </div>
                <FormDescription className="text-xs text-muted-foreground">
                  Organize content with tags
                </FormDescription>
                <FormMessage />
              </FormItem>
            )
          }}
        />

        {postId && (
          <div className="space-y-0.5">
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start"
              asChild
            >
              <Link to={`/${siteId}/editor/${type}/${postId}/metadata`}>
                <Search className="mr-2 h-4 w-4" />
                Metadata
              </Link>
            </Button>
            <p className="text-xs text-muted-foreground pt-1">
              Custom titles, descriptions and images for search engines, AI and
              social networks.
            </p>
          </div>
        )}

        <div className="flex gap-2 pt-4">
          {/* Removed Save button */}
          <Button
            type="button"
            variant="destructiveOutline"
            className="flex-1"
            disabled={isDeleting}
            onClick={() => {
              if (confirm('Are you sure you want to delete this post?')) {
                deletePost({ id: postId, siteId })
                if (type === 'page') navigate(`/${siteId}/pages`)
                else navigate(`/${siteId}/posts`)
              }
            }}
          >
            {isDeleting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="mr-2 h-4 w-4" />
            )}
            Delete post
          </Button>
        </div>
      </div>
    </div>
  )
}
